import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  Trophy,
  Medal,
  Crown,
  Star,
  Award,
  Flame,
  Users,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { leaderboardAPI } from '../lib/api';
import { toast } from 'sonner';

const departments = [
  'All Departments',
  'Engineering',
  'Marketing',
  'Sales',
  'Human Resources',
  'Finance',
  'Operations',
  'Product',
  'Design',
  'Customer Support',
];

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedDept, setSelectedDept] = useState('All Departments');
  const [userRank, setUserRank] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedDept]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const dept = selectedDept === 'All Departments' ? null : selectedDept;
      const response = await leaderboardAPI.get(dept, 50);
      setLeaderboard(response.data);

      // Find current user's rank
      const currentUserRank = response.data.find((entry) => entry.user_id === user?.id);
      setUserRank(currentUserRank);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-orange-500" />;
      default:
        return null;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-100 to-yellow-50 border-yellow-400';
      case 2:
        return 'bg-gradient-to-r from-gray-100 to-gray-50 border-gray-400';
      case 3:
        return 'bg-gradient-to-r from-orange-100 to-orange-50 border-orange-400';
      default:
        return 'bg-white';
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="leaderboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-sketch text-4xl text-ink">Leaderboard</h1>
            <p className="text-muted-foreground mt-1">
              Top learners in the organization
            </p>
          </div>
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="input-sketchy w-48" data-testid="dept-filter">
              <SelectValue placeholder="Filter by department" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => (
                <SelectItem key={dept} value={dept}>
                  {dept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* User's Rank Card */}
        {userRank && (
          <div className="card-sketchy bg-gradient-to-r from-primary/10 to-blue-50" data-testid="user-rank-card">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white font-bold text-2xl border-4 border-white shadow-lg">
                #{userRank.rank}
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Your Rank</p>
                <p className="font-bold text-xl text-ink">{userRank.name}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-2xl font-bold text-primary">
                  <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                  {userRank.points}
                </div>
                <p className="text-sm text-muted-foreground">points</p>
              </div>
            </div>
          </div>
        )}

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* Second Place */}
            <div className="card-sketchy text-center pt-8 bg-gradient-to-b from-gray-100 to-white order-1">
              <div className="relative inline-block">
                <Avatar className="w-20 h-20 border-4 border-gray-400 mx-auto">
                  <AvatarFallback className="bg-gray-200 text-xl font-bold">
                    {getInitials(leaderboard[1]?.name)}
                  </AvatarFallback>
                </Avatar>
                <Medal className="absolute -bottom-2 -right-2 w-8 h-8 text-gray-400" />
              </div>
              <p className="font-bold text-ink mt-4">{leaderboard[1]?.name}</p>
              <p className="text-sm text-muted-foreground">{leaderboard[1]?.department || 'No dept'}</p>
              <p className="font-bold text-2xl text-gray-600 mt-2">{leaderboard[1]?.points} pts</p>
            </div>

            {/* First Place */}
            <div className="card-sketchy text-center bg-gradient-to-b from-yellow-100 to-white -mt-4 order-2">
              <Crown className="w-10 h-10 text-yellow-500 mx-auto mb-2" />
              <div className="relative inline-block">
                <Avatar className="w-24 h-24 border-4 border-yellow-400 mx-auto">
                  <AvatarFallback className="bg-yellow-100 text-2xl font-bold">
                    {getInitials(leaderboard[0]?.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <p className="font-bold text-lg text-ink mt-4">{leaderboard[0]?.name}</p>
              <p className="text-sm text-muted-foreground">{leaderboard[0]?.department || 'No dept'}</p>
              <p className="font-bold text-3xl text-yellow-600 mt-2">{leaderboard[0]?.points} pts</p>
            </div>

            {/* Third Place */}
            <div className="card-sketchy text-center pt-12 bg-gradient-to-b from-orange-100 to-white order-3">
              <div className="relative inline-block">
                <Avatar className="w-16 h-16 border-4 border-orange-400 mx-auto">
                  <AvatarFallback className="bg-orange-100 font-bold">
                    {getInitials(leaderboard[2]?.name)}
                  </AvatarFallback>
                </Avatar>
                <Medal className="absolute -bottom-2 -right-2 w-7 h-7 text-orange-500" />
              </div>
              <p className="font-bold text-ink mt-4">{leaderboard[2]?.name}</p>
              <p className="text-sm text-muted-foreground">{leaderboard[2]?.department || 'No dept'}</p>
              <p className="font-bold text-xl text-orange-600 mt-2">{leaderboard[2]?.points} pts</p>
            </div>
          </div>
        )}

        {/* Full Leaderboard List */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="spinner" />
          </div>
        ) : leaderboard.length > 0 ? (
          <div className="card-sketchy">
            <div className="space-y-2">
              {leaderboard.slice(3).map((entry) => (
                <div
                  key={entry.user_id}
                  className={`leaderboard-row ${
                    entry.user_id === user?.id ? 'bg-primary/10 border-primary' : ''
                  }`}
                  data-testid={`leaderboard-entry-${entry.user_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600">
                    {entry.rank}
                  </div>
                  <Avatar className="w-10 h-10 border-2 border-black">
                    <AvatarFallback className="bg-primary/10 font-bold text-sm">
                      {getInitials(entry.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-ink truncate">{entry.name}</p>
                    <p className="text-xs text-muted-foreground">{entry.department || 'No dept'}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Award className="w-4 h-4 text-purple-500" />
                      <span>{entry.badges_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Flame className="w-4 h-4 text-orange-500" />
                      <span>{entry.courses_completed}</span>
                    </div>
                    <div className="w-20 text-right">
                      <span className="font-bold text-primary">{entry.points}</span>
                      <span className="text-muted-foreground text-xs ml-1">pts</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="card-sketchy text-center py-16">
            <Users className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-sketch text-2xl text-ink mb-2">No data yet</h3>
            <p className="text-muted-foreground">
              Start learning to appear on the leaderboard!
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
